import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Shield, FileText, CheckCircle, AlertTriangle, XCircle, Calendar, TrendingUp } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Badge, Table, Tabs } from '../../components/common';
import { complianceApi } from '../../services/api';
import './Compliance.css';

export default function ComplianceDashboard() {
    const [activeTab, setActiveTab] = useState('overview');

    const { data: overview } = useQuery({
        queryKey: ['compliance', 'overview'],
        queryFn: () => complianceApi.getOverview(),
        select: (res) => res.data?.data || {}
    });

    const { data: policies } = useQuery({
        queryKey: ['compliance', 'policies'],
        queryFn: () => complianceApi.listPolicies(),
        select: (res) => res.data?.data || []
    });

    const { data: checks } = useQuery({
        queryKey: ['compliance', 'checks'],
        queryFn: () => complianceApi.listChecks(),
        select: (res) => res.data?.data || []
    });

    const getStatusBadge = (status) => {
        const config = {
            'active': { variant: 'success', label: 'Active' },
            'pending_review': { variant: 'warning', label: 'Pending Review' },
            'inactive': { variant: 'default', label: 'Inactive' },
            'passed': { variant: 'success', label: 'Passed' },
            'failed': { variant: 'danger', label: 'Failed' },
            'warning': { variant: 'warning', label: 'Warning' }
        };
        const c = config[status] || { variant: 'default', label: status };
        return <Badge variant={c.variant}>{c.label}</Badge>;
    };

    const tabs = [
        { key: 'overview', label: 'Overview' },
        { key: 'policies', label: 'Policies' },
        { key: 'checks', label: 'Compliance Checks' }
    ];

    const policyColumns = [
        { key: 'code', title: 'Code', dataIndex: 'code', width: 100 },
        { key: 'name', title: 'Policy Name', dataIndex: 'name' },
        { key: 'category', title: 'Category', dataIndex: 'category' },
        { key: 'version', title: 'Version', dataIndex: 'version', width: 80 },
        { key: 'status', title: 'Status', dataIndex: 'status', render: (s) => getStatusBadge(s) },
        {
            key: 'reviewDate', title: 'Review Date', dataIndex: 'reviewDate',
            render: (d) => new Date(d).toLocaleDateString()
        }
    ];

    const checkColumns = [
        { key: 'entityType', title: 'Entity', dataIndex: 'entityType' },
        {
            key: 'entityId', title: 'ID', dataIndex: 'entityId',
            render: (id) => <code>{id}</code>
        },
        { key: 'checkType', title: 'Check Type', dataIndex: 'checkType' },
        { key: 'status', title: 'Status', dataIndex: 'status', render: (s) => getStatusBadge(s) },
        { key: 'message', title: 'Message', dataIndex: 'message' },
        {
            key: 'checkedAt', title: 'Checked', dataIndex: 'checkedAt',
            render: (d) => new Date(d).toLocaleString()
        }
    ];

    return (
        <div className="compliance-page">
            <PageHeader
                title="Compliance & Governance"
                subtitle="Policies, audits, and compliance monitoring"
                breadcrumbs={[{ label: 'Compliance', path: '/compliance' }]}
            />

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

            {activeTab === 'overview' && (
                <div className="compliance-overview">
                    <div className="compliance-stats">
                        <Card className="stat-card">
                            <CardBody>
                                <div className="stat-icon policies">
                                    <FileText size={24} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{overview?.policies?.total || 0}</span>
                                    <span className="stat-label">Policies</span>
                                    <span className="stat-sub">{overview?.policies?.active || 0} active</span>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="stat-card">
                            <CardBody>
                                <div className="stat-icon checks">
                                    <CheckCircle size={24} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{overview?.complianceChecks?.passRate || 0}%</span>
                                    <span className="stat-label">Pass Rate</span>
                                    <span className="stat-sub">{overview?.complianceChecks?.total || 0} total checks</span>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="stat-card">
                            <CardBody>
                                <div className="stat-icon audits">
                                    <Shield size={24} />
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{overview?.audits?.completed || 0}</span>
                                    <span className="stat-label">Audits Completed</span>
                                    <span className="stat-sub">{overview?.audits?.inProgress || 0} in progress</span>
                                </div>
                            </CardBody>
                        </Card>

                        <Card className="stat-card">
                            <CardBody>
                                <div className={`stat-icon risk ${overview?.riskLevel?.toLowerCase()}`}>
                                    {overview?.riskLevel === 'Low' ? <CheckCircle size={24} /> :
                                        overview?.riskLevel === 'High' ? <XCircle size={24} /> :
                                            <AlertTriangle size={24} />}
                                </div>
                                <div className="stat-content">
                                    <span className="stat-value">{overview?.riskLevel || 'N/A'}</span>
                                    <span className="stat-label">Risk Level</span>
                                    <span className="stat-sub">Overall assessment</span>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    <div className="compliance-grid">
                        <Card>
                            <CardBody>
                                <h3>Upcoming Audits</h3>
                                <div className="audit-schedule">
                                    <div className="schedule-item">
                                        <Calendar size={16} />
                                        <span>Next Audit: {overview?.nextAuditDate ? new Date(overview.nextAuditDate).toLocaleDateString() : 'TBD'}</span>
                                    </div>
                                    <div className="schedule-item">
                                        <Calendar size={16} />
                                        <span>Last Audit: {overview?.lastAuditDate ? new Date(overview.lastAuditDate).toLocaleDateString() : 'Never'}</span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <h3>Quick Actions</h3>
                                <div className="quick-actions">
                                    <Link to="/compliance?tab=policies" className="action-link">
                                        <FileText size={16} /> View All Policies
                                    </Link>
                                    <Link to="/compliance?tab=checks" className="action-link">
                                        <CheckCircle size={16} /> View Compliance Checks
                                    </Link>
                                    <Link to="/audit-logs" className="action-link">
                                        <TrendingUp size={16} /> View Audit Logs
                                    </Link>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            )}

            {activeTab === 'policies' && (
                <Card>
                    <Table
                        columns={policyColumns}
                        data={policies || []}
                        rowKey="id"
                        emptyText="No policies defined"
                    />
                </Card>
            )}

            {activeTab === 'checks' && (
                <Card>
                    <Table
                        columns={checkColumns}
                        data={checks || []}
                        rowKey="id"
                        emptyText="No compliance checks"
                    />
                </Card>
            )}
        </div>
    );
}
